import { useRevealOnScroll } from "../../lib/useRevealOnScroll";
import { cn } from "../../lib/utils";

/* ---------------- Card ---------------- */
export function Card({ className, children }) {
  const { ref, visible } = useRevealOnScroll();
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)]/92 p-4 shadow-[var(--shadow-card)] backdrop-blur-sm transition-all duration-200 sm:p-5",
        "hover:-translate-y-0.5 hover:border-[var(--color-border-strong)] hover:shadow-[var(--shadow-card-hover)]",
        visible ? "animate-card-in" : "card-pending",
        className
      )}
    >
      {children}
    </div>
  );
}

export function CardTitle({ children, meta }) {
  return (
    <div className="mb-4">
      <div className="text-[13.5px] font-semibold text-[var(--color-ink)]">
        {children}
      </div>
      {meta && (
        <div className="mt-0.5 text-[11.5px] text-[var(--color-ink-faint)]">
          {meta}
        </div>
      )}
    </div>
  );
}

/* ---------------- Section header (filete) ---------------- */
export function SectionHead({ title, sub }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-2.5">
      <span className="text-[12px] font-bold uppercase tracking-[0.09em] text-[var(--color-ink-soft)]">
        {title}
      </span>
      {sub && (
        <span className="text-[11.5px] text-[var(--color-ink-faint)]">
          {sub}
        </span>
      )}
      <span className="h-px flex-1 self-center bg-[var(--color-border)]" />
    </div>
  );
}

/* ---------------- Chip (faixa) ---------------- */
/* Texto na variante -ink, não na viva: aqui a cor fica sobre o fundo tonal do
   próprio status, e o par viva/tonal dá 4,1–4,5:1 — abaixo de AA para os 11px
   semibold do chip. Ver o bloco de status em tokens.css. */
const CHIP_CLS = {
  good: "bg-[var(--color-good-bg)] text-[var(--color-good-ink)]",
  warn: "bg-[var(--color-warn-bg)] text-[var(--color-warn-ink)]",
  bad: "bg-[var(--color-bad-bg)] text-[var(--color-bad-ink)]",
  /* ink-soft, e não ink-faint: aqui o texto fica sobre surface-3, não sobre o
     card branco. Com ink-faint o par dava 3,63:1 — abaixo de AA para os 11px
     semibold do chip. Com ink-soft, 5,33:1. */
  none: "bg-[var(--color-surface-3)] text-[var(--color-ink-soft)]"
};
export function Chip({ tone = "none", size = "sm", children }) {
  return (
    <span
      className={cn(
        // Sem borda e sem sombra: o chip já se distingue pelo fundo tonal, e
        // dentro dos cards da listagem (que no mobile já têm borda e sombra)
        // esses dois detalhes só somavam ruído.
        "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-semibold",
        size === "md" ? "px-3 py-1 text-[12.5px]" : "px-2 py-0.5 text-[11px]",
        CHIP_CLS[tone]
      )}
    >
      {children}
    </span>
  );
}

/* ---------------- Select ---------------- */
export function Select({ className, ...props }) {
  return (
    <select
      className={cn(
        "w-full min-w-0 cursor-pointer appearance-none truncate rounded-xl border border-transparent",
        "bg-[var(--color-surface-2)]/60 px-3 py-2 pr-9 text-[12px] text-[var(--color-ink-soft)]",
        "outline-none transition-all hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
        "focus:border-[var(--color-brand)] focus:bg-[var(--color-surface-2)] focus:text-[var(--color-ink)] focus:ring-2 focus:ring-[var(--color-brand)]/30",
        "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2210%22%20height%3D%226%22%20viewBox%3D%220%200%2010%206%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M1%201l4%204%204-4%22%20stroke%3D%22%23a8a29e%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22/%3E%3C/svg%3E')]",
        "bg-[length:10px] bg-[right_11px_center] bg-no-repeat",
        className
      )}
      {...props}
    />
  );
}

/* ---------------- Button ----------------

   A variante `solid` escreve em BRANCO, não no quase-preto de antes: o
   `text-[#06131c]` vinha do projeto de origem, onde a marca era um teal claro.
   Sobre o navy do AVA ele dava 2,22:1 — ilegível. Branco sobre navy dá 8,48:1. */
export function Button({ className, variant = "ghost", ...props }) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium transition-all",
        variant === "solid"
          ? "bg-[var(--color-brand)] text-white shadow-lg shadow-[var(--color-brand)]/15 hover:-translate-y-0.5 hover:bg-[var(--color-brand-dark)]"
          : "border border-[var(--color-border-strong)] bg-[var(--color-surface-2)] text-[var(--color-ink-soft)] hover:-translate-y-0.5 hover:bg-[var(--color-surface-3)] hover:text-[var(--color-ink)]",
        className
      )}
      {...props}
    />
  );
}

/* ---------------- Skeleton ---------------- */
export function Skeleton({ className }) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-[var(--radius-card)] bg-[var(--color-surface-3)]",
        className
      )}
    />
  );
}

/* ---------------- Estado vazio ---------------- */
export function EmptyState({ icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-[var(--radius-card)] border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)]/70 px-6 py-12 text-center shadow-[var(--shadow-card)]">
      {icon && (
        <div className="rounded-2xl bg-[var(--color-brand-dim)] p-3 text-[var(--color-brand)]">
          {icon}
        </div>
      )}
      <div className="text-[14px] font-medium text-[var(--color-ink-soft)]">
        {title}
      </div>
      {hint && (
        <div className="max-w-sm text-[12.5px] text-[var(--color-ink-faint)]">
          {hint}
        </div>
      )}
    </div>
  );
}

/* ---------------- Estado vazio compacto (dentro de card/gráfico) ----------------
 * Placeholder pra quando a consulta veio vazia (sem dado no período, lista sem
 * itens) — não confundir com EmptyState acima, que é pra falha de carregamento
 * da página inteira. Ícone contextual (por card) é obrigatório: reforça o "porquê"
 * junto do hint, em vez de só uma frase cinza solta. */
const ESTADO_VAZIO_TOM = {
  neutro: {
    bg: "var(--color-surface-3)",
    fg: "var(--color-ink-faint)",
    titulo: "var(--color-ink-soft)"
  },
  /* Os dois na cor viva, e não na -ink: aqui `bg` só pinta o quadradinho do
     ícone (elemento gráfico, 4,35:1 sobre o tonal) e `titulo` fica sobre o card
     BRANCO, não sobre o fundo tonal — ali a viva dá 5,02:1 e passa AA. */
  positivo: {
    bg: "var(--color-good-bg)",
    fg: "var(--color-good)",
    titulo: "var(--color-good)"
  }
};

export function EstadoVazio({
  icon,
  titulo,
  hint,
  tom = "neutro",
  altura = 200
}) {
  const cor = ESTADO_VAZIO_TOM[tom];
  return (
    <div
      className="grid place-items-center gap-2.5 px-4 text-center"
      style={{ height: altura }}
    >
      <div
        className="rounded-2xl p-2.5"
        style={{ background: cor.bg, color: cor.fg }}
      >
        {icon}
      </div>
      <div className="text-[13px] font-medium" style={{ color: cor.titulo }}>
        {titulo}
      </div>
      {hint && (
        <div className="max-w-[260px] text-[11.5px] leading-snug text-[var(--color-ink-faint)]">
          {hint}
        </div>
      )}
    </div>
  );
}
